import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calculator, History, Loader2, Save, Settings2, UserCog } from "lucide-react";
import MainLayout from "../components/CommonComponents/MainLayout";
import Pagination from "../components/CommonComponents/Pagination";
import { fetchTaxRate, fetchTaxRateLogs, updateTaxRate } from "../services/masterDataApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { tableBase, tableCell, tableContainer, tableHead, tableHeader, tableRow } from "../utils/tableStyle";
import CustomerDetailsTab from "./CustomerDetailsTab";

const LOGS_PER_PAGE = 10;

const formatPercent = (value) => `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const emptyTaxRate = {
  code: "VAT",
  name: "Value Added Tax",
  rate_percent: 12,
  status: "ACTIVE",
};

export default function MasterDataPage() {
  const [activeTab, setActiveTab] = useState("tax-rate");
  const [taxRate, setTaxRate] = useState(emptyTaxRate);
  const [form, setForm] = useState(emptyTaxRate);
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasChanges = useMemo(
    () =>
      Number(form.rate_percent || 0) !== Number(taxRate.rate_percent || 0) ||
      form.status !== taxRate.status,
    [form.rate_percent, form.status, taxRate.rate_percent, taxRate.status]
  );

  const loadTaxRate = async () => {
    setLoading(true);
    try {
      const response = await fetchTaxRate();
      const data = response.data?.data || emptyTaxRate;
      setTaxRate(data);
      setForm({
        code: data.code || emptyTaxRate.code,
        name: data.name || emptyTaxRate.name,
        rate_percent: data.rate_percent ?? emptyTaxRate.rate_percent,
        status: data.status || emptyTaxRate.status,
      });
    } catch (error) {
      console.error("Failed to load tax rate:", error);
      showErrorToast("Failed to load tax rate.");
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = useCallback(async (page = currentPage) => {
    setLogsLoading(true);
    try {
      const response = await fetchTaxRateLogs(page, LOGS_PER_PAGE);
      setLogs(response.data?.data || []);
      setTotalLogs(response.data?.pagination?.total || 0);
      setTotalPages(response.data?.pagination?.total_pages || 1);
    } catch (error) {
      console.error("Failed to load tax logs:", error);
      showErrorToast("Failed to load tax logs.");
    } finally {
      setLogsLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadTaxRate();
  }, []);

  useEffect(() => {
    loadLogs(currentPage);
  }, [currentPage, loadLogs]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await updateTaxRate({
        rate_percent: Number(form.rate_percent),
        status: form.status,
      });
      const data = response.data?.data || emptyTaxRate;
      setTaxRate(data);
      setForm({
        code: data.code || emptyTaxRate.code,
        name: data.name || emptyTaxRate.name,
        rate_percent: data.rate_percent ?? emptyTaxRate.rate_percent,
        status: data.status || emptyTaxRate.status,
      });
      showSuccessToast(response.data?.message || "Tax rate saved.");
      setCurrentPage(1);
      loadLogs(1);
    } catch (error) {
      console.error("Failed to save tax rate:", error);
      showErrorToast(error.response?.data?.message || "Failed to save tax rate.");
    } finally {
      setSaving(false);
    }
  };

  const startIndex = (currentPage - 1) * LOGS_PER_PAGE;

  return (
    <MainLayout>
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-7 px-4 py-8" style={{ background: "var(--color-hover)" }}>
        <section className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: "var(--color-muted)" }}>
              <Settings2 className="h-4 w-4" />
              Platform Master Data
            </div>
            <h1 className="mt-4 text-4xl font-bold tracking-tight" style={{ color: "var(--color-text)" }}>Master Data</h1>
            <p className="mt-3 max-w-3xl text-sm leading-6" style={{ color: "var(--color-muted)" }}>
              Manage shared platform records used across subscription billing, invoices, and customer account rules.
            </p>
          </div>
          {activeTab === "tax-rate" && (
            <div className="rounded-2xl border px-4 py-3 text-sm shadow-sm" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}>
              <span className="block text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: "var(--color-muted)" }}>Current VAT</span>
              <span className="mt-1 block text-2xl font-bold">{formatPercent(taxRate.rate_percent)}</span>
            </div>
          )}
        </section>

        <div className="flex flex-wrap gap-2 rounded-2xl border p-2 shadow-sm" style={{ background: "var(--color-bg)", borderColor: "var(--color-border)" }}>
          <button
            type="button"
            onClick={() => setActiveTab("tax-rate")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition ${activeTab === "tax-rate" ? "text-white shadow-sm" : ""}`}
            style={activeTab === "tax-rate"
              ? { background: "var(--color-primary)" }
              : { color: "var(--color-text)" }}
          >
            <Calculator className="h-4 w-4" />
            Tax Rate
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("staff-roles")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition ${activeTab === "staff-roles" ? "text-white shadow-sm" : ""}`}
            style={activeTab === "staff-roles"
              ? { background: "var(--color-primary)" }
              : { color: "var(--color-text)" }}
          >
            <UserCog className="h-4 w-4" />
            Staff Roles
          </button>
        </div>

        {activeTab === "tax-rate" ? (
          <>
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border p-5 shadow-sm"
              style={{ background: "var(--color-bg)", borderColor: "var(--color-border)" }}
            >
              <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold" style={{ color: "var(--color-text)" }}>Tax Rate</h2>
                  <p className="mt-1 text-sm" style={{ color: "var(--color-muted)" }}>
                    Set the platform VAT rate to use for subscription billing calculations.
                  </p>
                </div>
                {loading && (
                  <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--color-muted)" }}>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading
                  </span>
                )}
              </div>

              <fieldset disabled={loading || saving} className="grid gap-4 md:grid-cols-[1fr,1fr,180px,160px] disabled:opacity-70">
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Code</span>
                  <input value={form.code} readOnly className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-hover)", borderColor: "var(--color-border)", color: "var(--color-text)" }} />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Name</span>
                  <input value={form.name} readOnly className="w-full rounded-md border px-4 py-3 text-sm outline-none" style={{ background: "var(--color-hover)", borderColor: "var(--color-border)", color: "var(--color-text)" }} />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Rate Percent</span>
                  <input
                    name="rate_percent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.rate_percent}
                    onChange={handleChange}
                    className="w-full rounded-md border px-4 py-3 text-sm outline-none"
                    style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                    required
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-semibold" style={{ color: "var(--color-text)" }}>Status</span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className="w-full rounded-md border px-4 py-3 text-sm outline-none"
                    style={{ background: "var(--color-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <button
                  type="submit"
                  disabled={saving || !hasChanges}
                  className="btn-primary inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 md:col-span-4 md:w-fit"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Tax Rate
                </button>
              </fieldset>
            </form>

            <section className="flex flex-col gap-4" data-pagination-scroll-root>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-pagination-scroll-target>
                <div>
                  <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: "var(--color-muted)" }}>
                    <History className="h-4 w-4" />
                    Audit Trail
                  </div>
                  <h2 className="mt-2 text-xl font-semibold" style={{ color: "var(--color-text)" }}>Tax Logs</h2>
                </div>
                <span className="text-sm" style={{ color: "var(--color-muted)" }}>
                  Showing {totalLogs === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + LOGS_PER_PAGE, totalLogs)} of {totalLogs} logs
                </span>
              </div>

              <div className={`${tableContainer} shadow-sm`} style={{ border: "1px solid var(--color-border)" }}>
                <table className={tableBase}>
                  <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                    <tr>
                      <th className={tableHeader + " text-left"}>Date</th>
                      <th className={tableHeader + " text-left"}>Tax</th>
                      <th className={tableHeader + " text-left"}>Previous</th>
                      <th className={tableHeader + " text-left"}>New</th>
                      <th className={tableHeader + " text-left"}>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsLoading && (
                      <tr>
                        <td colSpan="5" className={tableCell + " py-8 text-center"} style={{ color: "var(--color-muted)" }}>Loading tax logs...</td>
                      </tr>
                    )}
                    {!logsLoading && logs.map((log) => (
                      <tr key={log.id} className={tableRow + " border-b last:border-0"} style={{ borderColor: "var(--color-border)" }}>
                        <td className={tableCell} style={{ color: "var(--color-text)" }}>{formatDateTime(log.created_at)}</td>
                        <td className={tableCell} style={{ color: "var(--color-text)" }}>
                          <div className="font-semibold">{log.code}</div>
                          <div className="text-xs" style={{ color: "var(--color-muted)" }}>{log.name}</div>
                        </td>
                        <td className={tableCell} style={{ color: "var(--color-text)" }}>{log.previous_rate_percent === null ? "-" : formatPercent(log.previous_rate_percent)}</td>
                        <td className={tableCell}>
                          <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            {formatPercent(log.new_rate_percent)}
                          </span>
                        </td>
                        <td className={tableCell} style={{ color: "var(--color-text)" }}>
                          {log.changed_by?.username || log.changed_by?.email || "System"}
                        </td>
                      </tr>
                    ))}
                    {!logsLoading && logs.length === 0 && (
                      <tr>
                        <td colSpan="5" className={tableCell + " py-8 text-center"} style={{ color: "var(--color-muted)" }}>No tax logs yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalLogs}
                onPageChange={setCurrentPage}
              />
            </section>
          </>
        ) : (
          <CustomerDetailsTab />
        )}
      </div>
    </MainLayout>
  );
}
